import {
  ArrowLeft,
  Menu,
  MessageSquare,
  MessageSquarePlus,
  PanelLeftClose,
  UploadCloud,
  Wrench,
} from "lucide-react";
import { useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  type ConversationBrand,
  IDLE_STREAM_STATE,
  useChatConversation,
  useChatConversations,
  useCreateChatConversation,
  useDeleteChatConversation,
  useStreamChatMessage,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { asConversationBrand, brandChatPath } from "./brand";
import { BrandSwitcher } from "./brand-switcher";
import { ConversationSidebar } from "./conversation-sidebar";
import { MessageInput, type MessageInputHandle } from "./message-input";
import { MessageList } from "./message-list";
import type { MachineSummary } from "./types";

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
      <div className="bg-accent-light text-primary mb-4 flex h-14 w-14 items-center justify-center rounded-full">
        <Wrench className="h-6 w-6" />
      </div>
      <h2 className="font-heading text-foreground text-lg font-semibold">Find the right Part ID</h2>
      <p className="text-muted mt-2 max-w-sm text-sm">
        Describe the part you need, or attach a service report or photo.
      </p>
    </div>
  );
}

interface NoConversationStateProps {
  /** Brand the view is scoped to (`?brand=`), or `null` when unscoped. */
  brand: string | null;
  /** Whether the (brand-filtered) sidebar has anything to pick from. */
  hasConversations: boolean;
}

/**
 * Bare `/chat` (no conversation selected — after deleting the open one, or
 * after switching brands). There's nothing to send a message to and a
 * conversation can't be created without a brand, so point the technician at the
 * brand picker. When a `?brand=` scope is in play, say so: the sidebar next to
 * this state only lists that brand's conversations, so "pick one from the list"
 * would be wrong advice once that filtered list is empty (e.g. right after
 * deleting the brand's last conversation).
 */
function NoConversationState({ brand, hasConversations }: NoConversationStateProps) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
      <div className="bg-accent-light text-primary mb-4 flex h-14 w-14 items-center justify-center rounded-full">
        <MessageSquarePlus className="h-6 w-6" />
      </div>
      <h2 className="font-heading text-foreground text-lg font-semibold">No conversation open</h2>
      <p className="text-muted mt-2 max-w-sm text-sm">
        {brand === null
          ? "Pick a machine brand to start a new conversation, or choose an existing one from the list."
          : hasConversations
            ? `Pick one of your ${brand} conversations from the list, or start a new chat.`
            : `You have no ${brand} conversations yet — start a new chat to begin.`}
      </p>
      <Link to="/select-brand" className="mt-6">
        <Button className="gap-2">
          <MessageSquarePlus className="h-4 w-4" />
          Start a new chat
        </Button>
      </Link>
    </div>
  );
}

export function ChatPage() {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // The open conversation lives in the URL (`/chat/:conversationId`) so a
  // reload / deep link restores it. Ids are UUID strings — a missing/empty
  // param means the empty `/chat` state; any non-empty string is a candidate id
  // and, if it doesn't resolve, the 404/403 redirect below sends us back to
  // `/chat` (no client-side format check needed).
  const activeId = conversationId ? conversationId : null;

  const conversationsQuery = useChatConversations();
  const conversationQuery = useChatConversation(activeId);
  const deleteConversation = useDeleteChatConversation();
  const createConversation = useCreateChatConversation();
  // Synchronous guard against a double-click firing two creates before React
  // re-renders (same reasoning as select-brand.tsx's isCreatingRef) — reset
  // after every attempt, unlike select-brand.tsx, because this component
  // stays mounted across `/chat/:id` -> `/chat/:id2` (a new conversation is
  // not a route change here), so a ref left permanently true would disable
  // "new chat" for the rest of the session after its first use.
  const isCreatingRef = useRef(false);
  const { send: sendMessage, state: rawStreamState, traces } = useStreamChatMessage();

  // The stream is a single global (one turn at a time), so scope it to the
  // conversation it belongs to. Without this, switching to another chat while a
  // turn is streaming would render that turn's loading/tool activity under the
  // wrong conversation.
  const streamState =
    rawStreamState.conversationId === activeId ? rawStreamState : IDLE_STREAM_STATE;

  const conversations = conversationsQuery.data ?? [];
  const detail = conversationQuery.data ?? null;
  const messages = detail?.messages ?? [];

  // ── Current brand ──────────────────────────────────────────────────────────
  // There is no global brand store on purpose: brand context lives in the URL,
  // like the open conversation. Two sources, in priority order:
  //
  //   1. The open conversation's own `brand` — authoritative, because a
  //      conversation is pinned to one brand for life server-side. Prefer the
  //      detail payload and fall back to the list summary so the brand doesn't
  //      flicker while the detail query refetches after switching chats.
  //   2. `?brand=` on bare `/chat` — the only way to know the brand when no
  //      conversation is open (after a brand switch or a delete). Ignored while
  //      a conversation is open, so a stale/hand-edited param can never
  //      contradict the conversation itself.
  //
  // `null` means "brand unknown" (a bare `/chat` with no param, or a deep link
  // whose conversation hasn't loaded yet): the sidebar stays unfiltered and the
  // switcher is hidden rather than guessing a brand.
  const brandParam = asConversationBrand(searchParams.get("brand"));
  const conversationBrand = asConversationBrand(
    detail?.brand ?? conversations.find((conversation) => conversation.id === activeId)?.brand,
  );
  const currentBrand = activeId !== null ? conversationBrand : brandParam;

  // Everything the sidebar shows belongs to the current brand — mixing both
  // catalogs in one list is what the per-row badge used to paper over. The
  // `brand` field is already on every summary, so this is a client-side filter
  // over the existing list query (no extra request).
  const visibleConversations =
    currentBrand === null
      ? conversations
      : conversations.filter((conversation) => conversation.brand === currentBrand);

  const handleSelect = (id: string) => {
    void navigate(`/chat/${id}`);
    setSidebarOpen(false);
  };

  // DISA and Wheelabrator technicians never work across brands, so "new
  // conversation" stays in whichever brand is already open rather than
  // sending them back through the picker every time — it only falls back to
  // the picker when no brand is known yet (bare `/chat`, no `?brand=`).
  const handleCreate = () => {
    if (currentBrand === null) {
      logger.debug("[chat]", "new conversation, no brand known → brand picker");
      void navigate("/select-brand");
      setSidebarOpen(false);
      return;
    }
    if (isCreatingRef.current) {
      logger.debug("[chat]", "ignoring duplicate new-chat click");
      return;
    }
    isCreatingRef.current = true;
    logger.debug("[chat]", "creating new conversation in current brand", { brand: currentBrand });
    createConversation
      .mutateAsync({ brand: currentBrand })
      .then((created) => {
        if (!created) {
          logger.error("[chat]", "failed to create conversation: empty response");
          return;
        }
        void navigate(`/chat/${created.id}`);
        setSidebarOpen(false);
      })
      .catch((error: unknown) => {
        logger.error("[chat]", "failed to create conversation", error);
      })
      .finally(() => {
        isCreatingRef.current = false;
      });
  };

  // Switching brand can't move the open conversation (its brand is immutable),
  // so it navigates to the other brand's conversation-less view: the sidebar
  // re-filters and the technician picks or starts a chat there.
  const handleSelectBrand = (brand: ConversationBrand) => {
    if (brand === currentBrand) {
      return;
    }
    logger.debug("[chat]", "switching brand", { from: currentBrand, to: brand });
    void navigate(brandChatPath(brand));
    setSidebarOpen(false);
  };

  const handleDelete = async (id: string) => {
    // Read the brand off the list BEFORE deleting — the delete invalidates the
    // conversations query, so afterwards the row (and its brand) is gone.
    const deletedBrand =
      asConversationBrand(conversations.find((conversation) => conversation.id === id)?.brand) ??
      (id === activeId ? currentBrand : null);
    try {
      await deleteConversation.mutateAsync({ conversationId: id });
      logger.debug("[chat]", "deleted conversation", id);
      // Deleting the open conversation returns to the empty state; deleting any
      // other one leaves the current URL untouched. Carry the deleted
      // conversation's brand into the URL so the view stays scoped — dropping to
      // a bare `/chat` would silently widen the sidebar back to both catalogs.
      if (id === activeId) {
        void navigate(deletedBrand ? brandChatPath(deletedBrand) : "/chat");
      }
    } catch (error) {
      logger.error("[chat]", "failed to delete conversation", error);
    }
  };

  // Auto-create-on-first-send is gone: a conversation can't exist without a
  // brand, so the composer is only rendered once `activeId` is set (the bare
  // `/chat` state shows the brand-picker CTA instead).
  const handleSend = async (content: string, files: File[]) => {
    if (activeId === null) {
      logger.error("[chat]", "no conversation available to send message");
      return;
    }
    try {
      logger.debug("[chat]", "sending message", {
        conversationId: activeId,
        hasContent: content.length > 0,
        fileCount: files.length,
      });
      await sendMessage({
        conversationId: activeId,
        content: content.length > 0 ? content : undefined,
        files: files.length > 0 ? files : undefined,
      });
    } catch (error) {
      logger.error("[chat]", "failed to send message", error);
    }
  };

  const handleSelectMachine = async (machine: MachineSummary) => {
    if (activeId === null) {
      return;
    }
    // The picker is attached to the assistant message that asked for
    // disambiguation. The technician's actual problem description lives in the
    // user message BEFORE that prompt — the click itself only carries the
    // chosen machine. Resend that description unmodified as `content` (used
    // purely for BOM search) and pass the chosen machine's numeric database id
    // as `machineId`. The backend sets the conversation's machine directly from
    // `machine_id`, so no fuzzy text resolution is needed — this works even
    // when the item_id isn't a plain number (e.g. `15EZX119`).
    const promptIndex = messages.findLastIndex(
      (m) => m.role === "assistant" && (m.needs_machine_selection?.length ?? 0) > 0,
    );
    const priorMessages = promptIndex >= 0 ? messages.slice(0, promptIndex) : messages;
    const originalUserMessage = priorMessages.findLast(
      (m) => m.role === "user" && !!m.content?.trim(),
    );
    const description = originalUserMessage?.content?.trim();
    logger.debug("[chat]", "resolving machine", {
      conversationId: activeId,
      machineId: machine.id,
      itemId: machine.item_id,
      hasDescription: !!description,
    });
    try {
      await sendMessage({
        conversationId: activeId,
        content: description,
        machineId: machine.id,
      });
    } catch (error) {
      logger.error("[chat]", "failed to resolve machine", error);
    }
  };

  const isThinking = streamState.isStreaming;
  const hasActiveConversation = activeId !== null;

  const activeMachineName = detail?.machine?.model_name ?? null;

  const messageInputRef = useRef<MessageInputHandle>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounterRef = useRef(0);

  // Dropping files only means something when there's a composer to hold them —
  // the bare `/chat` state renders the brand-picker CTA instead.
  const canAttach = hasActiveConversation && !isThinking;

  const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
    if (!canAttach) {
      return;
    }
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDraggingOver(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (!canAttach) {
      // No preventDefault — the browser shows its "drop not allowed" cursor,
      // consistent with the composer's own disabled state while sending.
      return;
    }
    event.preventDefault();
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingOver(false);
    if (!canAttach) {
      return;
    }
    const dropped = Array.from(event.dataTransfer.files);
    if (dropped.length > 0) {
      logger.debug("[chat]", "files dropped", { count: dropped.length });
      messageInputRef.current?.addFiles(dropped);
    }
  };

  // Deep link / reload to an id the backend rejects (not found, or not owned by
  // this user — including a malformed UUID): the query surfaces a 4xx. Redirect
  // to the empty state rather than leaving a broken page. Transient failures
  // (status 0, retried by the query client) are ignored so we don't bounce on a
  // blip.
  const queryStatus = (conversationQuery.error as { status?: number } | null)?.status;
  const isMissingConversation =
    conversationQuery.isError && (queryStatus === 404 || queryStatus === 403);
  if (isMissingConversation) {
    logger.debug("[chat]", "conversation not found, redirecting", { activeId, queryStatus });
    // Keep any brand scope that was already in the URL — the vanished
    // conversation can't supply one.
    return <Navigate to={brandParam ? brandChatPath(brandParam) : "/chat"} replace />;
  }

  return (
    <div className="bg-background flex h-screen flex-col">
      <header className="border-border bg-card flex shrink-0 items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label="Toggle conversation list"
          >
            {sidebarOpen ? <PanelLeftClose className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="text-primary flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            <span className="font-heading text-sm font-semibold">Spare Part ID Agent</span>
          </div>
          {/* Which brand catalog the chat is scoped to — the open
              conversation's own brand, or `?brand=` when none is open — plus the
              resolved machine once one is known. */}
          {currentBrand && (
            <div className="flex min-w-0 items-center gap-2">
              <BrandSwitcher brand={currentBrand} onSelect={handleSelectBrand} />
              {activeMachineName && (
                <span className="text-muted hidden truncate text-xs sm:inline">
                  {activeMachineName}
                </span>
              )}
            </div>
          )}
        </div>
        <Link to="/home">
          <Button variant="outline" size="sm" className="gap-1.5">
            <ArrowLeft className="h-4 w-4" />
            Home
          </Button>
        </Link>
      </header>

      <div className="relative flex min-h-0 flex-1">
        {/* Sidebar — persistent on md+, overlay drawer on mobile. */}
        <aside
          className={cn(
            "border-border bg-card z-20 w-72 shrink-0 border-r",
            "absolute inset-y-0 left-0 transition-transform md:static md:translate-x-0",
            sidebarOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <ConversationSidebar
            conversations={visibleConversations}
            activeId={activeId}
            brand={currentBrand}
            isLoading={conversationsQuery.isLoading}
            onSelect={handleSelect}
            onCreate={handleCreate}
            onDelete={handleDelete}
          />
        </aside>

        {/* Mobile overlay backdrop. */}
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Close conversation list"
            className="bg-foreground/20 absolute inset-0 z-10 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main chat pane. */}
        <main
          className="relative flex min-w-0 flex-1 flex-col"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isDraggingOver && (
            <div className="border-primary bg-primary/5 pointer-events-none absolute inset-2 z-30 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed">
              <UploadCloud className="text-primary h-8 w-8" />
              <p className="text-primary font-heading text-sm font-semibold">
                Drop files to attach
              </p>
            </div>
          )}
          {hasActiveConversation ? (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6">
                {conversationQuery.isLoading ? (
                  <EmptyState />
                ) : messages.length === 0 && !isThinking ? (
                  <EmptyState />
                ) : (
                  <div className="mx-auto max-w-2xl">
                    <MessageList
                      messages={messages}
                      // Guarded by `hasActiveConversation` (activeId !== null).
                      conversationId={activeId!}
                      isThinking={isThinking}
                      streamState={streamState}
                      traces={traces}
                      onSelectMachine={handleSelectMachine}
                    />
                  </div>
                )}
              </div>
              <div className="mx-auto w-full max-w-2xl">
                <MessageInput ref={messageInputRef} onSend={handleSend} disabled={isThinking} />
              </div>
            </>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <NoConversationState
                brand={currentBrand}
                hasConversations={visibleConversations.length > 0}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
